import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';
import { faDiscord, faPatreon, faPaypal } from '@fortawesome/free-brands-svg-icons';
import { faCheck, faInfoCircle, faTimes, faShoppingCart, faCog, faArrowRight, faChevronDown, faChevronUp, faInfinity } from '@fortawesome/free-solid-svg-icons';
import { Observable, Subscription } from 'rxjs';
import { shareReplay, take } from 'rxjs/operators';
import { BotGuild, GearInfo } from 'src/app/models/bot/guild';
import { BotResourceService } from 'src/app/services/bot-resource.service';
import { GuildService } from 'src/app/services/guild.service';
import { AppComponent } from 'src/app/app.component';

declare const Paddle: any;

interface ShopItem {
  price: number;
  id: string;
  viewValue: string;
  amount?: number;
  months?: number;
  internal: string;
}

interface GearContribution {
  value: number;
  id: string;
  viewValue: string;
}

interface PremiumMethod {
  id: 'gears' | 'paddle' | 'patreon';
  title: string;
  subtitle: string;
  icon: any;
  color: string;
  description: string;
  expanded: boolean;
}

interface FeatureRow {
  name: string;
  docUrl?: string;
  free: string | 'cross';
  premium: 'infinity' | 'check' | string;
}

@Component({
  selector: 'app-premium-unified',
  templateUrl: './premium-unified.component.html',
  styleUrls: ['./premium-unified.component.css']
})
export class PremiumUnifiedComponent implements OnInit, OnDestroy {
  faIcons = {
    patreon: faPatreon,
    discord: faDiscord,
    paypal: faPaypal,
    check: faCheck,
    cross: faTimes,
    info: faInfoCircle,
    shoppingCart: faShoppingCart,
    gear: faCog,
    arrowRight: faArrowRight,
    chevronDown: faChevronDown,
    chevronUp: faChevronUp,
    infinity: faInfinity
  };

  // Context detection
  guildId: string | null = null;
  guild: BotGuild | undefined;
  isGuildContext: boolean = false;
  isLoggedIn: boolean = false;
  gearInfo$: Observable<GearInfo> | undefined;

  // Premium methods
  premiumMethods: PremiumMethod[] = [
    {
      id: 'gears',
      title: 'Via Voting (Gears)',
      subtitle: 'Free Premium through community support',
      icon: faCog,
      color: '#7289DA',
      description: 'Get Premium features for free by voting for RaidOrganizer on bot listing sites.',
      expanded: false
    },
    {
      id: 'paddle',
      title: 'One-Time Payment',
      subtitle: 'PayPal, Credit Card, and more',
      icon: faPaypal,
      color: '#0070ba',
      description: 'One-time payments with no subscription. Perfect for those who prefer flexibility.',
      expanded: false
    },
    {
      id: 'patreon',
      title: 'Subscription',
      subtitle: 'Monthly Patreon membership',
      icon: faPatreon,
      color: '#f96854',
      description: 'Monthly subscription with exclusive Patreon benefits and Discord role.',
      expanded: false
    }
  ];

  featureCategories: { id: string; title: string; features: FeatureRow[] }[] = [
    {
      id: 'limits',
      title: 'Limits & Capacity',
      features: [
        { name: 'Max Event Channels', docUrl: 'https://docs.raidorganizer.org/getting-started/setup-channel', free: '2*', premium: 'infinity' },
        { name: 'Max Custom Signup Templates', docUrl: 'https://docs.raidorganizer.org/signup-templates', free: '1*', premium: 'infinity' },
        { name: 'Max Repeating Events', docUrl: 'https://docs.raidorganizer.org/repeating-events', free: '3*', premium: 'infinity' },
        { name: 'Max Event Templates', docUrl: 'https://docs.raidorganizer.org/event-templates', free: '3*', premium: 'infinity' },
        { name: 'Max Event Start Notifications', docUrl: 'https://docs.raidorganizer.org/notification', free: '1', premium: 'infinity' },
        { name: 'Max User Roles', docUrl: 'https://docs.raidorganizer.org/getting-started/terms/#user-role', free: '1', premium: '25' },
      ],
    },
    {
      id: 'scheduling',
      title: 'Events & Scheduling',
      features: [
        { name: 'Max Event Deletion Time', free: '14 days', premium: '60 days' },
        { name: 'Repeating Event Creation In Advance', free: '7 Days', premium: 'infinity' },
        { name: 'Repeating Event Interval', docUrl: 'https://docs.raidorganizer.org/repeating-events/create-repeating-events#custom-interval', free: '7 Days', premium: 'Custom' },
        { name: 'Command notify User Limit', docUrl: 'https://docs.raidorganizer.org/notification/notify/', free: '25 Users', premium: 'infinity' },
      ],
    },
    {
      id: 'automation',
      title: 'Automation & Notifications',
      features: [
        { name: 'Automatic Event Cancellation', docUrl: 'https://docs.raidorganizer.org/notification/event-cancel-notification', free: 'cross', premium: 'check' },
        { name: 'Automatic Voice Channel Creation', docUrl: 'https://docs.raidorganizer.org/voice-channel/', free: 'cross', premium: 'check' },
        { name: 'Custom notification Messages', docUrl: 'https://docs.raidorganizer.org/notification/', free: 'cross', premium: 'check' },
        { name: 'Log Channels', docUrl: 'https://docs.raidorganizer.org/voice-channel/', free: 'cross', premium: 'check' },
      ],
    },
    {
      id: 'customization',
      title: 'Customization & Appearance',
      features: [
        { name: 'Customize Bot Avatar and Banner', docUrl: 'https://docs.raidorganizer.org/premium/bot-branding', free: 'cross', premium: 'check' },
        { name: 'Event Images', free: 'cross', premium: 'check' },
        { name: 'Event Thumbnail', free: 'cross', premium: 'check' },
        { name: 'Event Footer Text & Icon', free: 'cross', premium: 'check' },
        { name: 'Event Overlap Notification', free: 'cross', premium: 'check' },
        { name: 'Remove Ads On Guild View', docUrl: 'https://docs.raidorganizer.org/misc/ads/', free: 'cross', premium: 'check' },
      ],
    },
    {
      id: 'extras',
      title: 'Extras',
      features: [
        { name: 'Signup Groups', docUrl: 'https://docs.raidorganizer.org/signup-groups/', free: 'cross', premium: 'check' },
      ],
    },
  ];

  // Shopping cart (guild context only)
  monthlyPremiumShopItems: ShopItem[] = [
    { price: 5, id: 'pri_01hjtw1z2904nsvvm52p2ba2za', viewValue: '1 Month Premium', months: 1, internal: '1-month-premium' },
    { price: 20, id: 'pri_01hjtxnjpgk1zp3feqnd2bg6hj', viewValue: '6 Months Premium', months: 6, internal: '6-months-premium' },
    { price: 38, id: 'pri_01hjtxpeanwgms957r4zwsxcwf', viewValue: '12 Months Premium', months: 12, internal: '12-months-premium' },
  ];

  lifetimePremiumShopItems: ShopItem[] = [
    { price: 80, id: 'pri_01hjtxqk831wxb35xtf596xd9y', viewValue: 'Lifetime Premium', internal: 'lifetime-premium' },
  ];

  premiumShopItems: ShopItem[] = this.monthlyPremiumShopItems.concat(this.lifetimePremiumShopItems);

  limitShopItems: ShopItem[] = [
    { price: 5, id: 'pri_01hjtxvtbkvwm32nv14var5zkv', viewValue: '+5 Event Channels', internal: '5-event-channels' },
    { price: 5, id: 'pri_01hjtxx5z8xgbc4pryj5pkvkd2', viewValue: '+5 Repeating Events', internal: '5-repeating-events' },
    { price: 5, id: 'pri_01hjty6wbrw6vss6vj0wpw8k8m', viewValue: '+3 Event Templates', internal: '3-event-templates' },
    { price: 5, id: 'pri_01hjty7xt391qdxg8zkq2eyyhf', viewValue: '+1 Signup Template', internal: '1-signup-template' },
    { price: 10, id: 'pri_01hjtye9p4wgpp62kn2zp4k5ze', viewValue: '+2 Event Channels, +2 Repeating Events, +2 Signup Templates', internal: '2-signup-templates-2-event-channels-2-repeating-events' },
    { price: 15, id: 'pri_01hjtytrz2k3ywx2qapem6mngv', viewValue: '+3 Event Channels, +3 Repeating Events, +3 Signup Templates', internal: '3-signup-templates-3-event-channels-3-repeating-events' },
  ];

  shopItems: ShopItem[] = this.premiumShopItems.concat(this.limitShopItems);
  checkoutList: ShopItem[] = [];
  checkoutListLength: number = 0;
  priceTotal: string | undefined;

  // Gear exchange (guild context only)
  gearFeatures: GearContribution[] = [
    { value: 15, id: 'add_repeating_event', viewValue: '+ 1 Max. repeating events' },
    { value: 15, id: 'add_channel', viewValue: '+ 1 Max. event channels' },
    { value: 25, id: 'add_event_template', viewValue: '+ 1 Max. event template' },
    { value: 75, id: 'add_signup_template', viewValue: '+ 1 Max. signup template' },
    { value: 25, id: '1_week_premium', viewValue: '+ 1 Premium week' },
    { value: 75, id: '4_weeks_premium', viewValue: '+ 4 Premium weeks' },
  ];
  selectedGearFeature: GearContribution | undefined;
  reminderOptions: number[] = [1, 2, 3, 4, 5, 6];
  premiumReminder: number | undefined;
  gearTransferAmount: number = 0;

  private guildSubscription: Subscription | undefined;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private oauthService: OAuthService,
    private guildService: GuildService,
    private botService: BotResourceService,
    private appComponent: AppComponent
  ) { }

  ngOnInit() {
    // Detect context - check parent route for guild ID (when accessed from /guilds/:id/premium)
    this.guildId = this.route.parent?.snapshot?.paramMap?.get('id') || this.route.snapshot.paramMap.get('id');
    this.isGuildContext = !!this.guildId;
    this.isLoggedIn = this.oauthService.hasValidAccessToken();

    if (this.isGuildContext && this.guildId) {
      this.loadGuildContext();
    }

    // Set default expanded method based on context
    if (this.isGuildContext) {
      this.premiumMethods[1].expanded = true; // Paddle by default in guild context
    } else {
      this.premiumMethods[0].expanded = true; // Gears by default in standalone
    }
  }

  ngOnDestroy() {
    if (this.guildSubscription) {
      this.guildSubscription.unsubscribe();
    }
  }

  private loadGuildContext() {
    if (!this.guildId) return;

    this.guildSubscription = this.guildService.getGuildData(this.guildId).subscribe(guild => {
      this.guild = guild;
    });

    this.gearInfo$ = this.botService.getGuildGear(this.guildId).pipe(
      shareReplay(1)
    );
  }

  toggleMethod(method: PremiumMethod) {
    // Close all others
    this.premiumMethods.forEach(m => {
      if (m.id !== method.id) {
        m.expanded = false;
      }
    });
    // Toggle current
    method.expanded = !method.expanded;
  }

  expandAndScrollToMethod(methodId: 'gears' | 'paddle' | 'patreon') {
    const method = this.premiumMethods.find(m => m.id === methodId);
    if (!method) return;

    this.premiumMethods.forEach(m => {
      m.expanded = m.id === methodId;
    });

    setTimeout(() => {
      const el = document.getElementById('premium-method-' + methodId);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }

  // Shopping cart methods
  addItemToCart(id: string) {
    const item = this.shopItems.find(item => item.id === id);
    if (!item) return;

    if (item.internal === 'lifetime-premium') {
      this.removePremiumItemsFromCart();
    }
    if (item.internal === '1-month-premium' || item.internal === '6-months-premium' || item.internal === '12-months-premium') {
      this.removeLifetimePremiumFromCart();
    }

    const itemInCart = this.checkoutList.find(item => item.id === id);
    if (itemInCart) {
      itemInCart.amount = (itemInCart.amount || 0) + 1;
    } else {
      item.amount = 1;
      this.checkoutList.push(item);
    }
    this.updateCart();
  }

  removeItemFromCart(id: string) {
    const itemInCart = this.checkoutList.find(item => item.id === id);
    if (itemInCart) {
      itemInCart.amount = (itemInCart.amount || 0) - 1;
      if (itemInCart.amount <= 0) {
        this.checkoutList = this.checkoutList.filter(item => item.id !== id);
      }
    }
    this.updateCart();
  }

  removePremiumItemsFromCart() {
    this.checkoutList = this.checkoutList.filter(item => 
      item.internal !== '1-month-premium' && 
      item.internal !== '6-months-premium' && 
      item.internal !== '12-months-premium'
    );
  }

  removeLifetimePremiumFromCart() {
    this.checkoutList = this.checkoutList.filter(item => item.internal !== 'lifetime-premium');
  }

  clearCart() {
    this.checkoutList = [];
    this.updateCart();
  }

  updateCart() {
    this.checkoutListLength = this.checkoutList.length;
    let total = 0;
    this.checkoutList.forEach(item => {
      total += item.price * (item.amount || 1);
    });
    this.priceTotal = total > 0 ? `${total} EUR` : undefined;
  }

  serverHasLifetimePremium(): boolean {
    if (this.guild?.lifetime_premium) {
      return true;
    }
    return !!this.checkoutList.find(item => item.internal === 'lifetime-premium');
  }

  checkout() {
    if (typeof Paddle === 'undefined' || !this.guildId) {
      return;
    }

    const products = this.checkoutList.map(item => ({
      priceId: item.id,
      quantity: item.amount || 1
    }));

    const userId = this.appComponent.userId;
    const userEmail = this.appComponent.email;

    if (!userId || !this.guildId) {
      return;
    }

    const checkoutConfig: any = {
      items: products,
      customData: {
        user_id: userId.toString(),
        guild_id: this.guildId.toString(),
      }
    };

    if (typeof userEmail === 'string' && userEmail.length > 0) {
      checkoutConfig.customer = {
        email: userEmail,
      };
    }

    Paddle.Checkout.open(checkoutConfig);
  }

  // Gear exchange methods
  onGearUserInput(event: Event) {
    if (!this.gearInfo$) return;
    
    const input = event.target as HTMLInputElement;
    const newValue = +input.value;
    
    this.gearInfo$.pipe(take(1)).subscribe(gearInfo => {
      if (newValue > gearInfo.user.curr_gears) {
        input.value = gearInfo.user.curr_gears.toString();
        this.gearTransferAmount = gearInfo.user.curr_gears;
      } else {
        this.gearTransferAmount = newValue;
      }
    });
  }

  contributeGears() {
    if (!this.guildId || !this.gearInfo$ || this.gearTransferAmount <= 0) return;
    
    this.botService.sendCommand(this.guildId, 'gears user contribute', this.gearTransferAmount.toString(), null, () => {
      this.gearInfo$!.pipe(take(1)).subscribe(gearInfo => {
        gearInfo.user.curr_gears -= this.gearTransferAmount;
        gearInfo.guild.curr_gears += this.gearTransferAmount;
        this.gearTransferAmount = 0;
      });
    });
  }

  consumeGears() {
    if (!this.selectedGearFeature || !this.guildId || !this.gearInfo$) return;

    this.botService.gearExchange(
      this.guildId,
      this.selectedGearFeature.viewValue,
      this.selectedGearFeature.id,
      this.premiumReminder,
      () => {
        this.gearInfo$!.pipe(take(1)).subscribe(gearInfo => {
          gearInfo.guild.curr_gears -= this.selectedGearFeature!.value;
          if (this.selectedGearFeature!.id === 'add_repeating_event') {
            gearInfo.limits.max_repeating_events += 1;
          } else if (this.selectedGearFeature!.id === 'add_channel') {
            gearInfo.limits.max_event_channels += 1;
          } else if (this.selectedGearFeature!.id === 'add_signup_template') {
            gearInfo.limits.max_custom_templates += 1;
          } else if (this.selectedGearFeature!.id === 'add_event_template') {
            gearInfo.limits.max_event_templates += 1;
          } else if (this.selectedGearFeature!.id.includes('premium')) {
            if (this.guild) {
              this.guild.has_premium = true;
            }
          }
          this.selectedGearFeature = undefined;
          this.premiumReminder = undefined;
        });
      }
    );
  }

  isReminderEnabled(): boolean {
    return !!(
      this.selectedGearFeature &&
      (this.selectedGearFeature.id === '1_week_premium' || this.selectedGearFeature.id === '4_weeks_premium')
    );
  }

  navigateToGuilds() {
    if (this.isLoggedIn) {
      this.router.navigate(['/guilds']);
    } else {
      this.router.navigate(['/']);
    }
  }

  navigateToGuildPremium() {
    if (this.guildId) {
      this.router.navigate(['/guilds', this.guildId, 'premium']);
    }
  }
}
